import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import styled, { keyframes } from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getFilteredFlows } from '@/common/utils'
import { selectProgress } from '@/store/modules/progress'
import type { RootState } from '@/store/redux-store'
import { FlowStages } from './flow-stages'
import { ProgressMenuItem } from './progress-menu-item'
import { Recommendations } from './recommendations'
import { ProgressFlow, ProgressFlows, RoundedCorner, ThemeOptions } from './types'

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const Root = styled.div``

const Container = styled.div<{ $noPadding: boolean }>`
  margin: 15px;
  padding: ${({ $noPadding }) => ($noPadding ? '0' : '1rem')};
  display: grid;
  grid-template-rows: minmax(min-content, max-content) 1fr;
  height: 95vh;
  background: white;
  overflow-y: hidden;
  box-shadow: 0 0 8px 2px rgba(100, 100, 100, 0.25);
  border-radius: 10px;

  @media (max-width: 800px) {
    max-height: 70vh;
    overflow-y: auto;
    box-shadow: none !important;
    border: none;
  }
`

const Header = styled.div`
  width: 100%;
  background-color: #052340;
  height: 65px;
  border-radius: 10px 10px 0 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 18px;
  justify-content: space-between;
`

const ArrowButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`

const LogoWrapper = styled.div`
  padding-top: 12px;
  width: 60%;
`

const MenuItems = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(75px, 1fr));
  margin: 0 15px;
  margin-top: 15px;
`

const ShopContainer = styled.div`
  animation: ${fadeIn} 0.25s ease-in-out 0s forwards 1;

  iframe {
    min-height: 95%;
    width: 100%;
    border: 0;
  }
`

const MENU_ITEMS = ['identity', 'residential', 'employment', 'financial']

export interface OverallProgressProps {
  themeOptions?: ThemeOptions
  onBack?: () => void
}

export const OverallProgress = ({ themeOptions, onBack }: OverallProgressProps) => {
  const progress = useSelector((state: RootState) => selectProgress(state))
  const [activeTab, setActiveTab] = useState('')
  const [shopOpen] = useState(false)
  const touchStartX = useRef<number | null>(null)
  const mainColor = themeOptions?.default?.mainColor
  const isGoodlordReferencingHeaderVisible = JSON.parse(
    localStorage.getItem('hasSummaryPage') ?? 'false',
  )

  const getFlows = useMemo(
    () => getFilteredFlows(progress.flows) as ProgressFlows,
    [progress.flows],
  )

  const getTab = useCallback(() => {
    const namedFlow = new URLSearchParams(window.location.search).get('flow')

    setActiveTab(
      Object.keys(progress.flows).reduce((active: string, flowKey: string) => {
        const flow = progress.flows[flowKey]

        if (namedFlow && namedFlow === flow.id && flow.completed === false) {
          return flowKey
        }

        if (active === '' && flow.completed === false) {
          return flowKey
        }

        return active
      }, ''),
    )
  }, [progress.flows])

  useEffect(() => {
    getTab()
  }, [getTab])

  const getPercentComplete = (key: string) => {
    const currFlow = progress.flows[key]
    if (!currFlow) {
      return 0
    }
    const actionsToComplete = currFlow.actions.filter(
      ({ completed }) => completed === false,
    )
    if (actionsToComplete.length === 0) {
      return 100
    }

    return 100 - (actionsToComplete.length / currFlow.actions.length) * 100
  }

  const isRounded = (): RoundedCorner => {
    const flows = Object.keys(getFlows)

    return flows.length === 1
      ? 'bottom'
      : activeTab === flows[0]
        ? 'right'
        : activeTab === flows[flows.length - 1]
          ? 'left'
          : 'both'
  }

  const setActive = (item: string) => {
    if (!item) return
    if (item === activeTab) {
      setActiveTab('')
      return
    }
    setActiveTab(item)
  }

  const handleSwipe = (direction: 'left' | 'right') => {
    const index = MENU_ITEMS.indexOf(activeTab)
    if (index === -1) return

    if (direction === 'left') {
      setActiveTab(
        index === MENU_ITEMS.length - 1 ? MENU_ITEMS[0] : MENU_ITEMS[index + 1],
      )
    } else {
      setActiveTab(
        index === 0 ? MENU_ITEMS[MENU_ITEMS.length - 1] : MENU_ITEMS[index - 1],
      )
    }
  }

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.changedTouches[0]?.clientX ?? null
  }

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return
    const endX = event.changedTouches[0]?.clientX ?? touchStartX.current
    const delta = endX - touchStartX.current
    touchStartX.current = null
    if (Math.abs(delta) < 40) return
    handleSwipe(delta < 0 ? 'left' : 'right')
  }

  const getShopUrl = () => {
    if (themeOptions?.config?.dashboardSettings?.customStoreUrl) {
      return themeOptions.config.dashboardSettings.customStoreUrl
    }
    return 'https://store.vouch.co.uk?src=bot'
  }

  if (Object.keys(progress.flows).length === 0) {
    return <Root id="overall-progress" />
  }

  return (
    <Root id="overall-progress">
      <Container $noPadding={isGoodlordReferencingHeaderVisible}>
        {isGoodlordReferencingHeaderVisible && (
          <Header>
            <ArrowButton
              type="button"
              onClick={() => onBack?.()}
              aria-label="Back to summary"
            >
              <FontAwesomeIcon icon="arrow-left" color="#FFFFFF" size="xl" />
            </ArrowButton>
            <LogoWrapper>
              <img
                src="/assets/img/referencing-logo.svg"
                alt="Goodlord Referencing Logo"
              />
            </LogoWrapper>
          </Header>
        )}
        <div>
          {!shopOpen && (
            <>
              <MenuItems>
                {Object.values(getFlows).map((flow: ProgressFlow, index) => (
                  <ProgressMenuItem
                    key={index}
                    iconName={flow.id ?? ''}
                    percent={getPercentComplete(flow.id ?? '')}
                    activeTab={activeTab === flow.id}
                    mainColor={mainColor}
                    onItemClicked={setActive}
                  />
                ))}
              </MenuItems>
              {activeTab !== '' && (
                <FlowStages
                  key={activeTab}
                  title={activeTab}
                  rounded={isRounded()}
                  flows={getFlows}
                  mainColor={mainColor}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                />
              )}
              <Recommendations
                recommendations={progress.recommendations}
                flows={progress.flows}
                mainColor={mainColor}
              />
            </>
          )}
          {shopOpen && (
            <ShopContainer>
              <iframe src={getShopUrl()} title="Store" />
            </ShopContainer>
          )}
        </div>
      </Container>
    </Root>
  )
}

export default OverallProgress
